import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Spin,
  Modal,
  Form,
  Input,
  message,
  Modal as AntModal,
} from 'antd';
import {
  PlusOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL, getAuthHeaders } from '../composant/ComposantAPP/Util';

const { Title } = Typography;

const AdminPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Récupération de l'admin connecté depuis localStorage
  const employerStr = localStorage.getItem('employer');
  const currentUser = employerStr ? JSON.parse(employerStr) : null;
  const currentUserId = currentUser?.id;

  // Récupération des admins
  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${BASE_URL}/admin/api/getAdmin`, { headers });
      setAdmins(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des admins :', error);
      message.error("Erreur lors du chargement des administrateurs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleAddAdmin = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BASE_URL}/admin/api/admin`, values, {
        headers: {
          'Authorization': 'Basic ' + token,
          'Content-Type': 'application/json',
        },
      });
      message.success('Administrateur ajouté avec succès');
      setModalVisible(false);
      form.resetFields();
      fetchAdmins();
    } catch (error) {
      console.error('Erreur lors de l’ajout de l’admin :', error);
      message.error("Erreur lors de l'ajout de l'administrateur");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDesactiverCompte = (userId) => {
    AntModal.confirm({
      title: 'Confirmer la désactivation',
      icon: <ExclamationCircleOutlined />,
      content: 'Êtes-vous sûr de vouloir désactiver ce compte ?',
      okText: 'Oui',
      cancelText: 'Non',
      onOk: async () => {
        try {
          const headers = getAuthHeaders();
          await axios.put(`${BASE_URL}/admin/api/desactiver-compte/${userId}`, null, { headers });
          message.success('Compte désactivé avec succès');
          fetchAdmins();
        } catch (error) {
          console.error('Erreur lors de la désactivation du compte :', error);
          message.error("Erreur lors de la désactivation du compte");
        }
      }
    });
  };

  // Fonction pour afficher ou masquer les infos selon l'id utilisateur
  const renderInfo = (text, record) => {
    if (String(record.userSystem?.id) === String(currentUserId)) {
      return <i>Informations privées</i>;
    }
    return text || '-';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',  // c'est l'id du poste/admin, tu peux le cacher si tu veux
      key: 'id',
      responsive: ['md'],
      render: (text, record) =>
        String(record.userSystem?.id) === String(currentUserId) ? <i>Privé</i> : text,
    },
    {
      title: 'Nom',
      dataIndex: ["userSystem", "nom"],
      key: 'nom',
      render: (text, record) => renderInfo(text, record),
    },
    {
      title: 'Prénom',
      dataIndex: ["userSystem", "prenom"],
      key: 'prenom',
      render: (text, record) => renderInfo(text, record),
    },
    {
      title: 'Téléphone',
      dataIndex: ["userSystem", "telephone"],
      key: 'telephone',
      render: (text, record) => renderInfo(text, record),
    },
    {
      title: 'Email',
      dataIndex: ["userSystem", "email"],
      key: 'email',
      responsive: ['lg'],
      render: (text, record) =>
        String(record.userSystem?.id) === String(currentUserId) ? (
          <i>Privé</i>
        ) : (
          <a href={`mailto:${text}`}>{text}</a>
        ),
    },
    {
      title: 'Poste',
      dataIndex: 'poste',
      key: 'poste',
      responsive: ['lg'],
      render: (text, record) => renderInfo(text, record),
    },
    {
      title: 'Statut',
      key: 'statut_compte',
      render: (_, record) => {
        if (String(record.userSystem?.id) === String(currentUserId)) {
          return <i>Privé</i>;
        }
        const statut = record.userSystem?.statut_compte;
        return (
          <Space>
            <span>{statut ? 'Actif' : 'Désactivé'}</span>
            {statut && (
              <Button
                type="primary"
                danger
                size="small"
                onClick={() => handleDesactiverCompte(record.userSystem?.id)}
                style={{ marginLeft: 8 }}
              >
                Désactiver
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Title level={3}>Liste des administrateurs</Title>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
          style={{ marginBottom: 16 }}
        >
          Ajouter un admin
        </Button>

        {loading ? (
          <Spin tip="Chargement des admins..." />
        ) : (
          <Table
            columns={columns}
            dataSource={admins}
            rowKey="id"
            pagination={{ pageSize: 8 }}
            bordered
            scroll={{ x: true }}
            size="middle"
          />
        )}
      </Space>

      <Modal
        title="Ajouter un administrateur"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddAdmin}
        >
          <Form.Item
            name="nom"
            label="Nom"
            rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="prenom"
            label="Prénom"
            rules={[{ required: true, message: 'Veuillez saisir le prénom' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="telephone"
            label="Téléphone"
            rules={[{ required: true, message: 'Veuillez saisir le téléphone' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Veuillez saisir l\'email' },
              { type: 'email', message: 'Email non valide' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="poste"
            label="Poste"
            rules={[{ required: true, message: 'Veuillez saisir le poste' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPage;
